import dayjs from "dayjs";
import type { TAddress } from "@/api/shared/address";
import { useChangeCase } from "@vueuse/integrations/useChangeCase.mjs";
import { NameFormatSetting, useSettingsStore } from "@/stores/settingsStore";
import type { TPracticeStaff } from "@/api/models/practice/practiceStaff";
import { i18n } from "@/locales";
import type { TDMScan, TDMScanFile } from "@/api/models/dm/scans";
import type { TPhone } from "@/api/shared/contacts.ts";

export const toCamelCase = (input?: string) => {
  if (!input) return "";
  const scrapedInput = input.split(".");
  return useChangeCase(scrapedInput[scrapedInput.length - 1]!, "camelCase").value;
};

export function strippedHtml(html?: string, maxLength?: number) {
  if (!html) {
    return "";
  }

  html = String(html);

  html = html
    .replace(/^b'|\\n'$/g, "")
    .replace(/\\n|\n/g, "")
    .replace(/<\/(\w+)>/g, "</$1> ")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return truncateString(html, maxLength);
}

export function getFixedDayjs(
  date: string | Date | null | undefined,
  format?: string,
  strict?: boolean
) {
  if (!date) return dayjs(date, format, strict); // Maintain dayjs behavior for null/undefined
  let d = dayjs(date, format, strict);

  if (typeof date === "string" && date.length >= 4) {
    if (format) {
      const yearIdx = format.indexOf("YYYY");
      if (yearIdx !== -1 && date.length >= yearIdx + 4) {
        const yearStr = date.substring(yearIdx, yearIdx + 4);
        if (yearStr.startsWith("00")) {
          const year = parseInt(yearStr);
          if (strict && !d.isValid()) {
            const spoofedDate =
              date.substring(0, yearIdx) +
              "20" +
              yearStr.substring(2) +
              date.substring(yearIdx + 4);
            const spoofedD = dayjs(spoofedDate, format, strict);
            if (spoofedD.isValid()) {
              d = spoofedD.year(year);
            }
          } else if (d.isValid() && d.year() === year + 1900) {
            d = d.year(year);
          }
        }
      }
    } else {
      const yearStr = date.substring(0, 4);
      if (yearStr.startsWith("00")) {
        const year = parseInt(yearStr);
        if (d.year() === year + 1900) {
          d = d.year(year);
        }
      }
    }
  }
  return d;
}

/**
 * Trailing explicit UTC offset (`\u00b1HH:mm` / `\u00b1HHmm`) embedded in an ISO datetime string, if any.
 * A bare `Z` is deliberately NOT treated as an offset to pin to: it's how Django (and most
 * APIs) mark plain UTC instants \u2014 `created_at`, `updated_at`, etc \u2014 which have no meaningful
 * "home" timezone and are expected to render in the viewer's own local time, same as always.
 * An explicit `\u00b1HH:mm` is different: it's a system (e.g. Cloud9) stating the actual wall-clock
 * offset of a specific place \u2014 a consultation slot's `-05:00` is the clinic's local time, and
 * that's what must be shown, not whatever timezone the viewer happens to be browsing from.
 */
function sourceOffsetOf(date: string): string | null {
  return /([+-]\d{2}:?\d{2})$/.exec(date)?.[0] ?? null;
}

/**
 * Parses a date pinned to the explicit UTC offset embedded in the source string itself (e.g. a
 * consultation slot's own `-05:00`), instead of dayjs's default of silently reprojecting onto
 * the viewer's browser timezone. Also applies `getFixedDayjs`'s year-spoofing fix. Falls back to
 * a plain `getFixedDayjs` parse for a Date/Dayjs, or a string with no explicit offset (`Z`/UTC
 * included \u2014 see `sourceOffsetOf`).
 */
export function parseWithSourceOffset(
  date: string | Date | null | undefined | dayjs.Dayjs,
  format?: string,
  strict?: boolean
) {
  const d = getFixedDayjs(date as string | Date | null | undefined, format, strict);
  const offset = typeof date === "string" ? sourceOffsetOf(date) : null;
  return offset ? d.utcOffset(offset) : d;
}

/** En dash used between the two ends of a collapsed range. */
const DATE_RANGE_DASH = "\u2013";

/**
 * Derive a numeric day/month pattern from a full display format, for ranges that state the
 * year once and so cannot reuse the format verbatim.
 *
 * Follows the user's own conventions: day/month order, separator, and zero-padding are all
 * read back off their chosen format, so `DD.MM.YYYY` yields `02.03` and `M/D/YYYY` yields `3/2`.
 * Word-month formats (`D MMM YYYY`) carry no numeric convention of their own, so they fall back
 * to unpadded numbers with a slash.
 */
function toNumericDayMonth(format: string): { format: string; yearSeparator: string } {
  const withoutYear = format
    .replace(/^YYYY[-/.,\s]+/, "")
    .replace(/[-/.,\s]+YYYY$/, "")
    .replace(/,?\s*YYYY\s*/, " ")
    .trim();

  const dayIndex = withoutYear.search(/D/);
  const monthIndex = withoutYear.search(/M/);
  const dayFirst = dayIndex >= 0 && monthIndex >= 0 && dayIndex < monthIndex;

  const wordMonth = /MMM/.test(format);
  const separator = withoutYear.match(/[-/.]/)?.[0] ?? "/";
  const dayToken = !wordMonth && /DD/.test(format) ? "DD" : "D";
  const monthToken = !wordMonth && /MM(?!M)/.test(format) ? "MM" : "M";

  // How the year attaches to the rest of the pattern, so the collapsed form keeps the user's
  // punctuation: "08/15/2026" stays slashed, "Aug 15, 2026" keeps its comma.
  const yearSeparator = /,\s*YYYY/.test(format) ? ", " : (format.match(/([-/.])YYYY/)?.[1] ?? " ");

  return {
    format: dayFirst
      ? `${dayToken}${separator}${monthToken}`
      : `${monthToken}${separator}${dayToken}`,
    yearSeparator,
  };
}

export function formatDateRange(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
  options?: {
    emptyValue?: string;
    separator?: string;
    /** dayjs format for each boundary; defaults to settings `dateFormatDisplay`. */
    format?: string;
  }
): string {
  const emptyValue = options?.emptyValue ?? "-";
  const separator = options?.separator ?? DATE_RANGE_DASH;
  const format = options?.format;

  const startD = start ? getFixedDayjs(start) : null;
  const endD = end ? getFixedDayjs(end) : null;
  const startValid = startD?.isValid() ? startD : null;
  const endValid = endD?.isValid() ? endD : null;

  if (!startValid && !endValid) {
    return "-";
  }

  // Same calendar month collapses to "10-15 Aug 2026" (or a single day when equal).
  if (startValid && endValid && startValid.isSame(endValid, "month")) {
    const monthYear = startValid.format("MMM YYYY");
    if (startValid.isSame(endValid, "day")) {
      return `${startValid.format("D")} ${monthYear}`;
    }
    return `${startValid.format("D")}-${endValid.format("D")} ${monthYear}`;
  }

  const resolvedFormat = format || useSettingsStore().dateFormatDisplay;

  // Different months inside one year state the year once, at the end. Both ends go numeric:
  // the year has to be lifted out of the pattern to be shared, and the remaining day/month
  // pair is the only part that still reads cleanly once it is gone. Word months are
  // deliberately not carried over here — "2 Mar - 15 Aug 2026" mixes a bare start against a
  // fully-qualified end, so the numeric pair is the consistent form.
  if (startValid && endValid && startValid.isSame(endValid, "year")) {
    const numeric = toNumericDayMonth(resolvedFormat);
    return `${startValid.format(numeric.format)} ${DATE_RANGE_DASH} ${endValid.format(
      numeric.format
    )}${numeric.yearSeparator}${endValid.format("YYYY")}`;
  }

  const formattedStart = startValid ? startValid.format(resolvedFormat) : null;
  const formattedEnd = endValid ? endValid.format(resolvedFormat) : null;

  return `${formattedStart ?? emptyValue} ${separator} ${formattedEnd ?? emptyValue}`;
}

export function toISODate(date: Date | string): string {
  return getFixedDayjs(date).format("YYYY-MM-DD");
}

export function formatDatetime(
  date: string | Date | null | undefined | dayjs.Dayjs,
  format?: string,
  useSourceOffset: boolean = false
) {
  if (!date) return "-";
  const d = useSourceOffset ? parseWithSourceOffset(date) : getFixedDayjs(date as string | Date);
  return d.format(format || useSettingsStore().displayDatetimeFormat);
}

export function formatDate(
  date: string | Date | null | undefined | dayjs.Dayjs,
  format?: string,
  useSourceOffset: boolean = false
) {
  if (!date) return "-";
  const d = useSourceOffset ? parseWithSourceOffset(date) : getFixedDayjs(date as string | Date);
  return d.isValid() ? d.format(format || useSettingsStore().dateFormatDisplay) : "-";
}

export function formatTimeOfDay(
  date: string | Date | null | undefined | dayjs.Dayjs,
  format?: string,
  useSourceOffset: boolean = false
) {
  if (!date) return "-";
  const d = useSourceOffset ? parseWithSourceOffset(date) : getFixedDayjs(date as string | Date);
  return d.isValid() ? d.format(format || useSettingsStore().hourFormatDisplay) : "-";
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";

  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function truncateString(str: string | null | undefined, maxLength?: number) {
  if (str && maxLength && str.length >= maxLength) {
    return `${str.slice(0, maxLength)}...`;
  }
  return str || "";
}

export enum EAddressFormatStyle {
  /** “123 Main St, Springfield, IL 62704” */
  US_STANDARD = "US_STANDARD",
  /** “Main St #123, Springfield, IL 62704” (route first, then number) */
  US_ROUTE_FIRST = "US_ROUTE_FIRST",
}

function clean(v?: string | null): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t ? t : null;
}

function join(items: Array<string | null | undefined>, sep = ", "): string {
  return items
    .filter((x): x is string => typeof x === "string" && x.trim().length > 0)
    .map((s) => s.trim())
    .join(sep);
}

function eq(a?: string | null, b?: string | null): string | null {
  const ca = clean(a);
  const cb = clean(b);
  if (!ca || !cb) return null;
  return ca.toLowerCase() === cb.toLowerCase() ? ca : null;
}

export function formatAddress(
  addr?: TAddress,
  multiline: boolean = false,
  style: EAddressFormatStyle = EAddressFormatStyle.US_STANDARD
): string {
  if (!addr) return "";

  const streetNumber = clean(addr.street_number);

  const streetAddress = clean(addr.street_address);
  const route = clean(addr.route);

  const subpremise = clean(addr.subpremise);
  const city = clean((addr.sublocality?.trim() ? addr.sublocality : addr.locality) ?? "");
  const state = clean(addr.administrative_area_level_1 ?? addr.postal_locality);
  const zip = clean(addr.postal_code);
  const country = clean(addr.country);

  const cityState = eq(city, state) ? city : join([city, state], ", ");

  let parts: Array<string | null>;

  switch (style) {
    case EAddressFormatStyle.US_ROUTE_FIRST: {
      const streetLine = streetAddress
        ? join([streetAddress, streetNumber, subpremise], " ")
        : join([route, streetNumber, subpremise], " ");

      parts = [streetLine, cityState, zip, country];
      break;
    }

    case EAddressFormatStyle.US_STANDARD: {
      const streetLine = streetAddress
        ? join([streetNumber, streetAddress, subpremise], " ")
        : join([streetNumber, route, subpremise], " ");

      const cityStateZip = join([cityState, zip], " ");
      parts = [streetLine, cityStateZip, country];
      break;
    }

    default: {
      const streetLine = streetAddress
        ? join([streetAddress, streetNumber, subpremise], " ")
        : join([route, streetNumber, subpremise], " ");

      // EU-style usually prefers "ZIP City"
      const postalCity = join([zip, city], " ");
      parts = [streetLine, postalCity, country];
      break;
    }
  }

  const filtered = parts.filter((x): x is string => !!x && x.trim().length > 0);
  return multiline ? filtered.join("\n") : filtered.join(", ");
}

export function getFileNameWoExt(fileName: string | null): string {
  if (!fileName) return "";

  const lastDotIndex = fileName.lastIndexOf(".");
  return lastDotIndex === -1 ? fileName : fileName.substring(0, lastDotIndex);
}

export function getFileSize(size?: number) {
  if (size == null) return "0";
  if (size < 1024) {
    return size + " B";
  }
  if (size < 1024 * 1024) {
    return (size / 1024).toFixed(2) + " KB";
  }
  return (size / 1024 / 1024).toFixed(2) + " MB";
}

export function formatName(entity: any) {
  return `${entity?.first_name} ${entity?.last_name}`;
}

export function stripPersonNameFromTitle(
  title: string | null | undefined,
  firstName?: string | null,
  lastName?: string | null
): string {
  if (!title) return "";

  const names = [firstName, lastName]
    .map((name) => name?.trim())
    .filter((name): name is string => !!name)
    .toSorted((a, b) => b.length - a.length)
    .map((name) => name.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`));

  if (names.length === 0) return title.trim();

  return title
    .replaceAll(new RegExp(String.raw`\s*-?\s*\b(?:${names.join("|")})\b`, "gi"), "")
    .trim();
}

export type NameFields = {
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
};

export function formatEntityName<T extends NameFields>(
  entity: T,
  setting: NameFormatSetting
): string {
  const firstName = entity.first_name?.trim() ?? "";
  const middleName = entity.middle_name?.trim() ?? "";
  const lastName = entity.last_name?.trim() ?? "";

  switch (setting) {
    case NameFormatSetting.LAST_FIRST:
      return [lastName, firstName].filter(Boolean).join(", ");

    case NameFormatSetting.FIRST_MIDDLE_LAST:
      return [firstName, middleName, lastName].filter(Boolean).join(" ");

    case NameFormatSetting.FIRST_LAST:
    default:
      return [firstName, lastName].filter(Boolean).join(" ");
  }
}

export function formatPrice(price: number) {
  const moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return moneyFormatter.format(price);
}

export function humanizeErrorMessage(
  t: (key: string) => string,
  errorMessage: string | null | undefined,
  fieldName: string | undefined,
  fieldLabel: string | undefined,
  type: "simple" | "array" = "simple"
): string | string[] {
  if (fieldName && errorMessage) {
    const tempName = fieldName?.replace(/\.(\d+)\./g, "[$1].").replace(/\.(\d+)$/, "[$1]");
    if (type === "simple") {
      return errorMessage.replace(tempName, fieldLabel ?? t(`generic.fields.${fieldName}`));
    } else {
      return errorMessage.replaceAll(tempName, fieldLabel ?? t(`generic.fields.${fieldName}`));
    }
  }
  return [];
}

export function formatForElementId(input: string): string {
  return input.replace(/[\s_]+/g, "-").toLowerCase();
}

export function formatToTitleCase(input: string): string {
  return input
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function formatPracticeStaffNameWithBadge(person?: TPracticeStaff | null): string {
  if (!person) return "";

  const badgeCodes = person.badges?.map((b) => b.code).filter(Boolean) ?? [];
  const badgeSuffix = badgeCodes.length ? `(${badgeCodes.join(", ")})` : "";

  return `${person.first_name} ${person.last_name} ${badgeSuffix}`.trim();
}

export function formatPracticeStaffListWithBadges(
  persons?: Array<TPracticeStaff> | null | undefined,
  separator: string = ", "
): string {
  return persons?.map(formatPracticeStaffNameWithBadge).filter(Boolean).join(separator) ?? "";
}

export function formatPracticeStaffPrimaryEmail(staff: TPracticeStaff): string {
  return staff.emails?.find((e) => e.primary)?.email ?? "";
}

export function formatPracticeStaffFirstPhone(staff: TPracticeStaff): string {
  const phone = staff.phones?.[0];
  if (!phone?.number?.trim()) return "";
  return phone.prefix ? `(${phone.prefix}) ${phone.number}` : phone.number;
}

export function formatPhone(phone: TPhone | null): string {
  if (!phone?.number?.trim()) return "";
  return phone.prefix ? `(${phone.prefix}) ${phone.number}` : phone.number;
}

export function formatDMScanPosition(DMScan: TDMScan | null): string[] {
  if (!DMScan) return [];

  return (DMScan.files ?? []).map((file: TDMScanFile) => {
    const position = file.position ?? file.name?.replace(/\.[^.]+$/, "") ?? "";
    const key = `models.patient.dmScans.position.${position}`;
    return i18n.global.te(key) ? String(i18n.global.t(key)) : position;
  });
}

export function toCssSize(value?: number | string) {
  if (value == null) return undefined;
  return typeof value === "number" ? `${value}px` : value;
}

export function formatCount(value: number | string | null | undefined): string | null {
  if (value == null || value === "") return null;
  return String(value);
}
